import { useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Upload, FileText, AlertTriangle, Copy } from 'lucide-react'
import { toast } from 'sonner'
import api from '@/services/api'

interface ImportResult {
  total: number
  imported: number
  duplicates: number
  errors: number
  details: {
    imported: { row: number; id: string; empresa: string }[]
    duplicates: { row: number; empresa: string; reason: string; existingId: string }[]
    errors: { row: number; empresa?: string; error: string }[]
  }
}

export function BdrImportCsv() {
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<ImportResult | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.name.endsWith('.csv')) {
      toast.error('Selecione um arquivo CSV')
      return
    }

    setLoading(true)
    setResult(null)

    const formData = new FormData()
    formData.append('file', file)

    try {
      const { data } = await api.post('/bdr/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      const res: ImportResult = data.data
      setResult(res)
      toast.success(`${res.imported} leads importados!`)
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Erro ao importar CSV')
    } finally {
      setLoading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const csvTemplate = `empresa,contato,email,telefone,cnpj,cargo,segmento,plataforma,modelo,faturamento,origem,problema
Empresa Exemplo,João Silva,joao@exemplo.com,11999999999,12345678000195,Diretor,E-commerce,Shopify,B2B,100k-500k,CSV Import,Interesse em ERP`

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--glass-bg)] border border-[var(--glass-border)] text-text-primary text-xs font-bold rounded-md hover:bg-white/[0.06] transition-all min-h-[44px]"
      >
        <Upload size={13} />
        Importar CSV
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => { setIsOpen(false); setResult(null) }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="glass-card border-white/[0.06] p-6 w-full max-w-lg mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2 text-text-primary font-extrabold text-sm uppercase tracking-widest mb-5">
                <FileText size={16} className="text-accent" />
                Importar Leads via CSV
              </div>

              {/* Template */}
              <div className="mb-4 p-3 bg-white/[0.02] border border-white/[0.04] rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold text-text-muted uppercase tracking-wider">Template CSV</span>
                  <button
                    onClick={() => navigator.clipboard.writeText(csvTemplate)}
                    className="text-[10px] font-bold text-accent hover:text-accent/80 flex items-center gap-1"
                  >
                    <Copy size={10} />
                    Copiar
                  </button>
                </div>
                <code className="text-[9px] text-text-muted break-all leading-relaxed block">
                  {csvTemplate.split('\n')[0]}
                </code>
              </div>

              {/* Upload */}
              <label className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-white/[0.08] rounded-xl cursor-pointer hover:border-accent/30 transition-all group">
                <Upload size={24} className="text-text-muted group-hover:text-accent transition-colors mb-2" />
                <span className="text-xs font-bold text-text-muted group-hover:text-text-primary transition-colors">
                  {loading ? 'Importando...' : 'Clique para selecionar CSV'}
                </span>
                <span className="text-[9px] text-text-muted mt-1">Máximo 500 registros, 5MB</span>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleFile}
                  disabled={loading}
                />
              </label>

              {/* Result */}
              {result && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 space-y-2"
                >
                  <div className="grid grid-cols-3 gap-2">
                    <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-center">
                      <div className="text-lg font-extrabold text-emerald-400">{result.imported}</div>
                      <div className="text-[9px] font-bold text-emerald-400/80 uppercase">Importados</div>
                    </div>
                    <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-center">
                      <div className="text-lg font-extrabold text-amber-400">{result.duplicates}</div>
                      <div className="text-[9px] font-bold text-amber-400/80 uppercase">Duplicatas</div>
                    </div>
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-center">
                      <div className="text-lg font-extrabold text-red-400">{result.errors}</div>
                      <div className="text-[9px] font-bold text-red-400/80 uppercase">Erros</div>
                    </div>
                  </div>

                  {result.details.errors.length > 0 && (
                    <div className="p-2 bg-red-500/5 border border-red-500/10 rounded-lg">
                      {result.details.errors.slice(0, 5).map((err, i) => (
                        <div key={i} className="flex items-center gap-1 text-[10px] text-red-400">
                          <AlertTriangle size={10} />
                          <span>Linha {err.row}: {err.error}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              <div className="flex justify-end mt-5">
                <button
                  onClick={() => { setIsOpen(false); setResult(null) }}
                  className="px-4 py-2 text-xs font-bold text-text-muted hover:text-text-primary transition-colors"
                >
                  Fechar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
